import React, { useState, useEffect, useMemo } from 'react';
import { ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import preplanningService, { ConfigArea } from '../../services/preplanning.service';
import TypeAhead from '../TypeAhead';
import api from '../../services/api';
import { cachedGet } from '../../utils/lookupCache';

// All config keys that can have region overrides
const CONFIG_KEYS = [
  'weight_distance',
  'weight_workload',
  'weight_profitability',
  'weight_driver_score',
  // Settings - Smart Dispatch only
  'timeout_default_minutes',
  'max_suggestions_per_move',
  // Settings - Pre-planning only
  'default_parking_location',
  'default_start_time',
  'default_drop_duration',
  'default_live_duration',
  'max_dispatches_local',
  'max_dispatches_otr',
  'max_returns_local',
  'max_returns_otr',
  'round_trip_live_count',
  'round_trip_drop_count',
  'require_preference_match',
  'low_load_mode',
  // Limits - both
  'distance_max_miles',
  'min_score_threshold',
  // Limits - Pre-planning only
  'return_max_miles',
  // Pay Goals - both
  'daily_goal_local',
  'daily_goal_otr',
] as const;

type ConfigKey = typeof CONFIG_KEYS[number];

interface ConfigData {
  [key: string]: {
    value: string;
    overrides: Array<{ region: string; value: string }>;
  };
}

export type ConfigMode = 'smart-dispatch' | 'smart-preplanning';

export interface SmartDispatchConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRegion: string;
  onSave: () => void;
  /** Modal title - defaults to "Smart Dispatch Configuration" */
  title?: string;
  /** Mode determines which fields are shown */
  mode: ConfigMode;
}

const SmartDispatchConfigModal: React.FC<SmartDispatchConfigModalProps> = ({
  isOpen,
  onClose,
  selectedRegion,
  onSave,
  title = 'Smart Dispatch Configuration',
  mode,
}) => {
  const isSmartDispatch = mode === 'smart-dispatch';
  const isPreplanning = mode === 'smart-preplanning';

  // Map UI mode to the backend's config_area column. Preplanning and dispatch
  // each store their own row per (config_key, region, driver_type) in the
  // smart_dispatch_config table — they no longer share values.
  const configArea: ConfigArea = isSmartDispatch ? 'dispatch' : 'preplanning';

  // Scoring weights
  const [weights, setWeights] = useState({
    distance: 0.30,
    workload: 0.25,
    profitability: 0.25,
    driver_score: 0.20,
  });

  // Global values (for showing when using global default)
  const [globalValues, setGlobalValues] = useState<Record<string, string>>({});

  // Smart Dispatch settings
  const [timeoutMinutes, setTimeoutMinutes] = useState(5);
  const [maxSuggestionsPerMove, setMaxSuggestionsPerMove] = useState(3);

  // Pre-planning settings
  const [defaultParkingLocation, setDefaultParkingLocation] = useState('');
  const [locations, setLocations] = useState<any[]>([]);
  const [defaultStartTime, setDefaultStartTime] = useState('06:00');
  const [defaultDropDuration, setDefaultDropDuration] = useState(45);
  const [defaultLiveDuration, setDefaultLiveDuration] = useState(90);
  const [maxDispatchesLocal, setMaxDispatchesLocal] = useState(8);
  const [maxDispatchesOtr, setMaxDispatchesOtr] = useState(3);
  const [maxReturnsLocal, setMaxReturnsLocal] = useState(2);
  const [maxReturnsOtr, setMaxReturnsOtr] = useState(1);
  const [roundTripLiveCount, setRoundTripLiveCount] = useState(1);
  const [roundTripDropCount, setRoundTripDropCount] = useState(2);
  const [requirePreferenceMatch, setRequirePreferenceMatch] = useState(true);
  const [lowLoadMode, setLowLoadMode] = useState(false);

  // Limits - both
  const [distanceMaxMiles, setDistanceMaxMiles] = useState(50);
  const [minScoreThreshold, setMinScoreThreshold] = useState(0.3);

  // Limits - Pre-planning only
  const [returnMaxMiles, setReturnMaxMiles] = useState(50);

  // Pay Goals
  const [dailyGoalLocal, setDailyGoalLocal] = useState(500);
  const [dailyGoalOtr, setDailyGoalOtr] = useState(800);

  // Convert locations to TypeAhead options
  const locationOptions = useMemo(() => {
    return locations.map((loc: any) => ({
      value: String(loc.recno),
      label: loc.cyalias && loc.cyalias.trim() ? loc.cyalias : loc.lname,
      description: `${loc.ladd}, ${loc.lcity}, ${loc.lstate}`,
    }));
  }, [locations]);

  // Default override state (all false = no region overrides)
  const defaultOverrideState: Record<ConfigKey, boolean> = CONFIG_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: false }),
    {} as Record<ConfigKey, boolean>
  );

  // Track which fields have region overrides (existing or user-modified)
  const [regionOverrides, setRegionOverrides] = useState<Record<ConfigKey, boolean>>(defaultOverrideState);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'weights' | 'settings' | 'limits' | 'pay'>('weights');

  // Load config when modal opens, region changes, or area changes
  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen, selectedRegion, configArea]);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load full config with overrides info, scoped to this modal's area
      const fullConfig = await preplanningService.getFullConfig(selectedRegion || undefined, configArea);
      const configData: ConfigData = fullConfig.config;

      // Helper to check if a region override exists
      const hasRegionOverride = (key: string): boolean => {
        if (!selectedRegion) return false;
        const item = configData[key];
        if (!item?.overrides) return false;
        return item.overrides.some((o) => o.region === selectedRegion);
      };

      // Helper to get effective value
      const getValue = (key: string, defaultValue: string): string => {
        const item = configData[key];
        if (!item) return defaultValue;

        if (selectedRegion && item.overrides) {
          const override = item.overrides.find((o) => o.region === selectedRegion);
          if (override) return override.value;
        }

        return item.value || defaultValue;
      };

      // Store global values for reference
      const globals: Record<string, string> = {};
      for (const key of CONFIG_KEYS) {
        globals[key] = configData[key]?.value || '';
      }
      setGlobalValues(globals);

      // Set values - Weights
      setWeights({
        distance: parseFloat(getValue('weight_distance', '0.30')),
        workload: parseFloat(getValue('weight_workload', '0.25')),
        profitability: parseFloat(getValue('weight_profitability', '0.25')),
        driver_score: parseFloat(getValue('weight_driver_score', '0.20')),
      });

      // Smart Dispatch settings
      setTimeoutMinutes(parseInt(getValue('timeout_default_minutes', '5'), 10));
      setMaxSuggestionsPerMove(parseInt(getValue('max_suggestions_per_move', '3'), 10));

      // Pre-planning settings
      setDefaultParkingLocation(getValue('default_parking_location', ''));
      setDefaultStartTime(getValue('default_start_time', '06:00'));
      setDefaultDropDuration(parseInt(getValue('default_drop_duration', '45'), 10));
      setDefaultLiveDuration(parseInt(getValue('default_live_duration', '90'), 10));
      setMaxDispatchesLocal(parseInt(getValue('max_dispatches_local', '8'), 10));
      setMaxDispatchesOtr(parseInt(getValue('max_dispatches_otr', '3'), 10));
      setMaxReturnsLocal(parseInt(getValue('max_returns_local', '2'), 10));
      setMaxReturnsOtr(parseInt(getValue('max_returns_otr', '1'), 10));
      setRoundTripLiveCount(parseInt(getValue('round_trip_live_count', '1'), 10));
      setRoundTripDropCount(parseInt(getValue('round_trip_drop_count', '2'), 10));
      setRequirePreferenceMatch(getValue('require_preference_match', 'true').toLowerCase() === 'true');
      setLowLoadMode(getValue('low_load_mode', 'false').toLowerCase() === 'true');

      // Limits
      setDistanceMaxMiles(parseInt(getValue('distance_max_miles', '50'), 10));
      setMinScoreThreshold(parseFloat(getValue('min_score_threshold', '0.3')));
      setReturnMaxMiles(parseInt(getValue('return_max_miles', '50'), 10));

      // Pay Goals
      setDailyGoalLocal(parseFloat(getValue('daily_goal_local', '500')));
      setDailyGoalOtr(parseFloat(getValue('daily_goal_otr', '800')));

      // Set override states
      const overrideState: Record<ConfigKey, boolean> = {} as Record<ConfigKey, boolean>;
      for (const key of CONFIG_KEYS) {
        overrideState[key] = hasRegionOverride(key);
      }
      setRegionOverrides(overrideState);

      // Fetch locations for parking location TypeAhead
      try {
        const locResponse = await cachedGet<unknown[]>('/admin/locations');
        setLocations((locResponse.data as never[]) || []);
      } catch (locErr) {
        console.error('Failed to load locations:', locErr);
      }

    } catch (err) {
      console.error('Failed to load config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const total = weights.distance + weights.workload + weights.profitability + weights.driver_score;
  const isWeightsValid = lowLoadMode || Math.abs(total - 1.0) < 0.001;

  // Mark a config key as modified (creates a region override)
  const markAsModified = (key: ConfigKey) => {
    setRegionOverrides((prev) => ({ ...prev, [key]: true }));
  };

  const handleSave = async () => {
    if (!isWeightsValid) {
      setError('Scoring weights must sum to 1.0');
      return;
    }
    if (!selectedRegion) {
      setError('A region must be selected to save configuration');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updates: Promise<unknown>[] = [];

      // Build config values map
      const configValues: Record<string, string> = {
        weight_distance: weights.distance.toFixed(2),
        weight_workload: weights.workload.toFixed(2),
        weight_profitability: weights.profitability.toFixed(2),
        weight_driver_score: weights.driver_score.toFixed(2),
        // Smart Dispatch settings
        timeout_default_minutes: String(timeoutMinutes),
        max_suggestions_per_move: String(maxSuggestionsPerMove),
        // Pre-planning settings
        default_parking_location: defaultParkingLocation,
        default_start_time: defaultStartTime,
        default_drop_duration: String(defaultDropDuration),
        default_live_duration: String(defaultLiveDuration),
        max_dispatches_local: String(maxDispatchesLocal),
        max_dispatches_otr: String(maxDispatchesOtr),
        max_returns_local: String(maxReturnsLocal),
        max_returns_otr: String(maxReturnsOtr),
        round_trip_live_count: String(roundTripLiveCount),
        round_trip_drop_count: String(roundTripDropCount),
        require_preference_match: String(requirePreferenceMatch),
        low_load_mode: String(lowLoadMode),
        // Limits
        distance_max_miles: String(distanceMaxMiles),
        min_score_threshold: minScoreThreshold.toFixed(2),
        return_max_miles: String(returnMaxMiles),
        // Pay Goals
        daily_goal_local: dailyGoalLocal.toFixed(2),
        daily_goal_otr: dailyGoalOtr.toFixed(2),
      };

      // Only save values that have been marked as overrides (existing or user-modified)
      for (const key of CONFIG_KEYS) {
        if (regionOverrides[key]) {
          updates.push(preplanningService.updateConfig(key, configValues[key], selectedRegion, configArea));
        }
      }

      await Promise.all(updates);

      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save config:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Alt+S saves, Alt+C closes — same guards as the footer buttons, so an invalid
  // weight set can't be saved by keyboard either.
  useAltHotkeys(
    {
      S: !isWeightsValid || saving || loading ? null : handleSave,
      C: saving ? null : onClose,
    },
    undefined,
    { layer: isOpen },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const tabClasses = (tab: string) =>
    `px-3 py-2 text-sm font-medium rounded-t-lg transition-colors ${
      activeTab === tab
        ? 'bg-panel text-blue-600 dark:text-blue-400 border-b-2 border-blue-600'
        : 'text-muted hover:text-gray-700 dark:hover:text-gray-300'
    }`;

  // Helper component to show whether value is using global default or region override
  const ConfigValueIndicator: React.FC<{ configKey: ConfigKey; formatValue?: (val: string) => string }> = ({ configKey, formatValue }) => {
    const hasOverride = regionOverrides[configKey];
    const displayValue = formatValue ? formatValue(globalValues[configKey] || '') : globalValues[configKey];
    return (
      <div className="text-xs mt-1">
        {hasOverride ? (
          <span className="text-blue-600 dark:text-blue-400">
            Region override for {selectedRegion}
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">
            Using global default: {displayValue}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-scrim/50 transition-opacity" onClick={onClose} />

        <div
          ref={panelRef}
          style={panelStyle}
          className="relative bg-panel rounded-lg shadow-xl max-w-lg w-full"
        >
          {/* Header */}
          <ModalHeader title={title} onClose={onClose} dragHandleProps={dragHandleProps} />
          <p className="px-4 py-1.5 text-sm text-muted border-b border-gray-200 dark:border-gray-700">
            Region: <span className="text-blue-600 dark:text-blue-400 font-medium">{selectedRegion}</span>
          </p>

          {/* Tabs */}
          <div className="flex gap-1 px-4 pt-2 bg-gray-50 dark:bg-gray-900">
            <button className={tabClasses('weights')} onClick={() => setActiveTab('weights')}>
              Scoring Weights
            </button>
            <button className={tabClasses('settings')} onClick={() => setActiveTab('settings')}>
              Settings
            </button>
            <button className={tabClasses('limits')} onClick={() => setActiveTab('limits')}>
              Limits
            </button>
            <button className={tabClasses('pay')} onClick={() => setActiveTab('pay')}>
              Pay Goals
            </button>
          </div>

          {/* Content */}
          <div className="p-4 min-h-[320px]">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                {/* Weights Tab */}
                {activeTab === 'weights' && (
                  <div className="space-y-4">
                    {/* Low Load Mode Toggle - Pre-planning only */}
                    {isPreplanning && (
                      <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <label className="flex items-center justify-between text-sm font-medium text-label">
                          <div>
                            <span>Low Load - Fill Top Drivers</span>
                            <p className="text-xs font-normal text-muted mt-1">
                              Fills highest-rated drivers to capacity first, then moves to next. Best for low-volume days.
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={lowLoadMode}
                              onChange={(e) => { setLowLoadMode(e.target.checked); markAsModified('low_load_mode'); }}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-amber-500"></div>
                          </label>
                        </label>
                        <ConfigValueIndicator configKey="low_load_mode" />
                      </div>
                    )}

                    <p className={`text-sm text-gray-500 dark:text-gray-400 ${lowLoadMode ? 'opacity-40' : ''}`}>
                      Adjust how different factors influence driver selection. Values must sum to 1.0.
                    </p>

                    <div className={`space-y-3 ${lowLoadMode ? 'opacity-40 pointer-events-none' : ''}`}>
                      <div>
                        <label className="flex justify-between text-sm font-medium text-label mb-1">
                          <span>Distance (proximity)</span>
                          <span>{weights.distance.toFixed(2)}</span>
                        </label>
                        <input
                          type="range" min="0" max="1" step="0.05"
                          value={weights.distance}
                          onChange={(e) => { setWeights((w) => ({ ...w, distance: parseFloat(e.target.value) })); markAsModified('weight_distance'); }}
                          className="w-full"
                        />
                        <ConfigValueIndicator configKey="weight_distance" />
                      </div>

                      <div>
                        <label className="flex justify-between text-sm font-medium text-label mb-1">
                          <span>Workload Balance</span>
                          <span>{weights.workload.toFixed(2)}</span>
                        </label>
                        <input
                          type="range" min="0" max="1" step="0.05"
                          value={weights.workload}
                          onChange={(e) => { setWeights((w) => ({ ...w, workload: parseFloat(e.target.value) })); markAsModified('weight_workload'); }}
                          className="w-full"
                        />
                        <ConfigValueIndicator configKey="weight_workload" />
                      </div>

                      <div>
                        <label className="flex justify-between text-sm font-medium text-label mb-1">
                          <span>Profitability (daily goal)</span>
                          <span>{weights.profitability.toFixed(2)}</span>
                        </label>
                        <input
                          type="range" min="0" max="1" step="0.05"
                          value={weights.profitability}
                          onChange={(e) => { setWeights((w) => ({ ...w, profitability: parseFloat(e.target.value) })); markAsModified('weight_profitability'); }}
                          className="w-full"
                        />
                        <ConfigValueIndicator configKey="weight_profitability" />
                      </div>

                      <div>
                        <label className="flex justify-between text-sm font-medium text-label mb-1">
                          <span>Driver Score (performance)</span>
                          <span>{weights.driver_score.toFixed(2)}</span>
                        </label>
                        <input
                          type="range" min="0" max="1" step="0.05"
                          value={weights.driver_score}
                          onChange={(e) => { setWeights((w) => ({ ...w, driver_score: parseFloat(e.target.value) })); markAsModified('weight_driver_score'); }}
                          className="w-full"
                        />
                        <ConfigValueIndicator configKey="weight_driver_score" />
                      </div>
                    </div>

                    <div className={`text-sm font-medium ${isWeightsValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'} ${lowLoadMode ? 'opacity-40' : ''}`}>
                      Total: {total.toFixed(2)} {!isWeightsValid && '(must equal 1.0)'}
                    </div>
                  </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted">
                      Settings for Smart {isSmartDispatch ? 'Dispatch' : 'Pre-Planning'} functionality.
                    </p>

                    <div className="space-y-4">
                      {/* Smart Dispatch only */}
                      {isSmartDispatch && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-label mb-1">
                              Assignment Timeout (minutes)
                            </label>
                            <input
                              type="number" min="1" max="30"
                              value={timeoutMinutes}
                              onChange={(e) => { setTimeoutMinutes(parseInt(e.target.value, 10) || 5); markAsModified('timeout_default_minutes'); }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                            />
                            <p className="text-xs text-muted mt-1">
                              Time before a suggestion expires
                            </p>
                            <ConfigValueIndicator configKey="timeout_default_minutes" />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-label mb-1">
                              Max Suggestions Per Move
                            </label>
                            <input
                              type="number" min="1" max="10"
                              value={maxSuggestionsPerMove}
                              onChange={(e) => { setMaxSuggestionsPerMove(parseInt(e.target.value, 10) || 3); markAsModified('max_suggestions_per_move'); }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                            />
                            <p className="text-xs text-muted mt-1">
                              Number of driver suggestions to show per move
                            </p>
                            <ConfigValueIndicator configKey="max_suggestions_per_move" />
                          </div>
                        </>
                      )}

                      {/* Pre-planning only */}
                      {isPreplanning && (
                        <>
                          {/* Require Preference Match - Top Position */}
                          <div>
                            <label className="flex items-center justify-between text-sm font-medium text-label mb-1">
                              <span>Require Preference Match</span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={requirePreferenceMatch}
                                  onChange={(e) => { setRequirePreferenceMatch(e.target.checked); markAsModified('require_preference_match'); }}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                              </label>
                            </label>
                            <p className="text-xs text-muted mt-1">
                              Allow cross-preference matching when disabled
                            </p>
                            <ConfigValueIndicator configKey="require_preference_match" />
                          </div>

                          {/* Low Load Mode */}
                          <div>
                            <label className="flex items-center justify-between text-sm font-medium text-label mb-1">
                              <span>Low Load Mode</span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={lowLoadMode}
                                  onChange={(e) => { setLowLoadMode(e.target.checked); markAsModified('low_load_mode'); }}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                              </label>
                            </label>
                            <p className="text-xs text-muted mt-1">
                              Fill top-performing drivers first instead of spreading work evenly
                            </p>
                            <ConfigValueIndicator configKey="low_load_mode" />
                          </div>

                          {/* Default Parking & Start Time - Side by Side */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-label mb-1">
                                Default Parking Location
                              </label>
                              <TypeAhead
                                options={locationOptions}
                                value={defaultParkingLocation}
                                onChange={(val: string) => { setDefaultParkingLocation(val); markAsModified('default_parking_location'); }}
                                placeholder="Search location..."
                                showSearch
                                showDescriptions
                              />
                              <ConfigValueIndicator
                                configKey="default_parking_location"
                                formatValue={(val) => {
                                  if (!val) return '';
                                  const loc = locations.find(l => String(l.recno) === val);
                                  return loc ? (loc.cyalias && loc.cyalias.trim() ? loc.cyalias : loc.lname) : val;
                                }}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-label mb-1">
                                Default Start Time
                              </label>
                              <input
                                type="time"
                                value={defaultStartTime}
                                onChange={(e) => { setDefaultStartTime(e.target.value); markAsModified('default_start_time'); }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                              />
                              <ConfigValueIndicator configKey="default_start_time" />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-label mb-3">
                              Default Stop Duration (minutes)
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  Drop
                                </label>
                                <input
                                  type="number" min="15" max="180"
                                  value={defaultDropDuration}
                                  onChange={(e) => { setDefaultDropDuration(parseInt(e.target.value, 10) || 45); markAsModified('default_drop_duration'); }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                                />
                                <ConfigValueIndicator configKey="default_drop_duration" />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  Live
                                </label>
                                <input
                                  type="number" min="30" max="300"
                                  value={defaultLiveDuration}
                                  onChange={(e) => { setDefaultLiveDuration(parseInt(e.target.value, 10) || 90); markAsModified('default_live_duration'); }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                                />
                                <ConfigValueIndicator configKey="default_live_duration" />
                              </div>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-label mb-3">
                              Round-Trip Dispatch Count
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  Drop
                                </label>
                                <input
                                  type="number" min="1" max="10"
                                  value={roundTripDropCount}
                                  onChange={(e) => { setRoundTripDropCount(parseInt(e.target.value, 10) || 2); markAsModified('round_trip_drop_count'); }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                                />
                                <ConfigValueIndicator configKey="round_trip_drop_count" />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  Live
                                </label>
                                <input
                                  type="number" min="1" max="10"
                                  value={roundTripLiveCount}
                                  onChange={(e) => { setRoundTripLiveCount(parseInt(e.target.value, 10) || 1); markAsModified('round_trip_live_count'); }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                                />
                                <ConfigValueIndicator configKey="round_trip_live_count" />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Limits Tab */}
                {activeTab === 'limits' && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted">
                      Set dispatch and distance limits for driver eligibility.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-label mb-3">
                          Max Deliveries Per Driver
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Local
                            </label>
                            <input
                              type="number" min="1" max="20"
                              value={maxDispatchesLocal}
                              onChange={(e) => { setMaxDispatchesLocal(parseInt(e.target.value, 10) || 8); markAsModified('max_dispatches_local'); }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                            />
                            <ConfigValueIndicator configKey="max_dispatches_local" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              OTR
                            </label>
                            <input
                              type="number" min="1" max="10"
                              value={maxDispatchesOtr}
                              onChange={(e) => { setMaxDispatchesOtr(parseInt(e.target.value, 10) || 3); markAsModified('max_dispatches_otr'); }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                            />
                            <ConfigValueIndicator configKey="max_dispatches_otr" />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-label mb-3">
                          Max Returns Per Driver
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Local
                            </label>
                            <input
                              type="number" min="0" max="10"
                              value={maxReturnsLocal}
                              onChange={(e) => { setMaxReturnsLocal(parseInt(e.target.value, 10) || 0); markAsModified('max_returns_local'); }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                            />
                            <ConfigValueIndicator configKey="max_returns_local" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              OTR
                            </label>
                            <input
                              type="number" min="0" max="10"
                              value={maxReturnsOtr}
                              onChange={(e) => { setMaxReturnsOtr(parseInt(e.target.value, 10) || 0); markAsModified('max_returns_otr'); }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                            />
                            <ConfigValueIndicator configKey="max_returns_otr" />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-label mb-1">
                          Max Distance (miles)
                        </label>
                        <input
                          type="number" min="10" max="200"
                          value={distanceMaxMiles}
                          onChange={(e) => { setDistanceMaxMiles(parseInt(e.target.value, 10) || 50); markAsModified('distance_max_miles'); }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                        />
                        <p className="text-xs text-muted mt-1">
                          Distance score drops to 0 beyond this distance
                        </p>
                        <ConfigValueIndicator configKey="distance_max_miles" />
                      </div>

                      {/* Pre-planning only */}
                      {isPreplanning && (
                        <div>
                          <label className="block text-sm font-medium text-label mb-1">
                            Max Return Distance (miles)
                          </label>
                          <input
                            type="number" min="5" max="100"
                            value={returnMaxMiles}
                            onChange={(e) => { setReturnMaxMiles(parseInt(e.target.value, 10) || 50); markAsModified('return_max_miles'); }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                          />
                          <p className="text-xs text-muted mt-1">
                            Max distance to slot a return after a delivery
                          </p>
                          <ConfigValueIndicator configKey="return_max_miles" />
                        </div>
                      )}

                      <div>
                        <label className="flex justify-between text-sm font-medium text-label mb-1">
                          <span>Min Score Threshold</span>
                          <span>{minScoreThreshold.toFixed(2)}</span>
                        </label>
                        <input
                          type="range" min="0" max="1" step="0.05"
                          value={minScoreThreshold}
                          onChange={(e) => { setMinScoreThreshold(parseFloat(e.target.value)); markAsModified('min_score_threshold'); }}
                          className="w-full"
                        />
                        <p className="text-xs text-muted mt-1">
                          Drivers below this score are not considered
                        </p>
                        <ConfigValueIndicator configKey="min_score_threshold" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Pay Goals Tab */}
                {activeTab === 'pay' && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted">
                      Set daily earning goals for driver types. Used for profitability scoring.
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-label mb-1">
                          Local Daily Goal ($)
                        </label>
                        <input
                          type="number" min="0" max="2000" step="25"
                          value={dailyGoalLocal}
                          onChange={(e) => { setDailyGoalLocal(parseFloat(e.target.value) || 500); markAsModified('daily_goal_local'); }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                        />
                        <ConfigValueIndicator configKey="daily_goal_local" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-label mb-1">
                          OTR Daily Goal ($)
                        </label>
                        <input
                          type="number" min="0" max="2000" step="25"
                          value={dailyGoalOtr}
                          onChange={(e) => { setDailyGoalOtr(parseFloat(e.target.value) || 800); markAsModified('daily_goal_otr'); }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading"
                        />
                        <ConfigValueIndicator configKey="daily_goal_otr" />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {error && <div className="text-sm text-red-600 dark:text-red-400 mt-4">{error}</div>}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isWeightsValid || saving || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {saving ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <CheckIcon className="h-4 w-4" />
              )}
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartDispatchConfigModal;
